import type {SketchRelationContainer} from '../../sketch/solver/constraint/SketchRelationContainer';
import {SketchEntityKind} from '../../sketch/solver/constraint/relation-compatibility/SketchEntityKinds';
import type {SketchArcObject} from "./SketchArcObject";
import type {SketchLineObject} from "./SketchLineObject";
import type {SketchSplineObject} from "./SketchSplineObject";
import type {SketchStyleSplineObject} from "./SketchStyleSplineObject";
import type {SketchParabolaObject} from './SketchParabolaObject';
import type {SketchEllipseObject} from './SketchEllipseObject';

import type {SketchCloneContext} from './SketchCloneContext';


/**
 * 所有草图网格对象统一实现的接口。
 */
export interface ISketchObject {
    uuid: string
    /**
     * 跨会话稳定持久 ID（`cad:sketch-entity:<uuid>` 格式）。
     * 说明：
     * 1. 与 Three.js 运行时 `uuid` 严格区分：`uuid` 随对象重建而变，`persistentId` 表达“同一逻辑实体”；
     * 2. `cloneSelf` 系复制（历史快照/恢复）必须继承该 ID；复制出新实体的流程必须显式重新生成；
     * 3. 该 ID 是几何关系、尺寸和 `.puppy` 持久化的唯一引用锚点。
     */
    persistentId: string;
    /** 草图对象运行时类型。 */
    objectType: SketchEntityKind;
    /** 与对象关联的约束容器。 */
    constraints: SketchRelationContainer;
    /** 是否为封闭几何。 */
    isClosedGeometry: boolean;
    isSketchObject: boolean;
    beginDragPreview: () => void;
    endDragPreview: () => void;
    /**
     * 判断当前对象是否允许拖动。
     * @returns 允许拖动返回 true，否则返回 false。
     */
    canDrag: () => boolean;

    /**
     * 复制当前对象，返回与源对象完全解耦的新实例。
     * 说明：
     * 1. 返回对象与源对象不共享可变几何/点对象引用；
     * 2. 复合对象可通过 `context` 复用同一批点副本，保持新对象内部拓扑关系；
     * 3. `context` 仅在复制流程内传递，不会写入对象状态。
     * @param context 复制上下文。
     * @returns 当前对象的独立副本。
     */
    cloneSelf(context?: SketchCloneContext): ISketchObject;

}

export type CollectSource = SketchArcObject
    | SketchEllipseObject
    | SketchSplineObject
    | SketchStyleSplineObject
    | SketchLineObject
    | SketchParabolaObject

/**
 * 草图对象关联目标类型。
 * 说明：
 * 1. 点对象可关联单个草图对象；
 * 2. 也可关联封闭集合对象；
 * 3. 用于替代将关联关系写入运行时扩展容器。
 */
export type SketchRelatedObject = ISketchObject | ISketchCollect;

// 多个草图对象的容器容器
export interface ISketchCollect {
    uuid: string
    /** 跨会话稳定持久 ID；语义与 `ISketchObject.persistentId` 一致。 */
    persistentId: string;
    /** 草图对象运行时类型。 */
    objectType: SketchEntityKind;
    /** 是否为封闭几何。 */
    isClosedGeometry: boolean;
    isSketchObject: boolean;
    collectSet: Set<CollectSource>;
    beginDragPreview: () => void;
    endDragPreview: () => void;
    /**
     * 判断当前集合对象是否允许拖动。
     * @returns 允许拖动返回 true，否则返回 false。
     */
    canDrag: () => boolean;
    /**
     * 复制当前集合对象，返回与源对象完全解耦的新实例。
     * @param context 复制上下文。
     * @returns 当前集合对象的独立副本。
     */
    cloneSelf(context?: SketchCloneContext): ISketchCollect;

}

/**
 * 草图点类型枚举。
 * 说明：
 * 1. 点统一使用点对象表达；
 * 2. 只区分控制点与草图点。
 */
export enum SketchPointKind {
    /** 控制点。依附于某个几何实体（线/弧/圆等）的附属锚点 */
    ControlPoint = 'controlPoint',
    /** 草图点。用户主动绘制的独立草图点 */
    SketchPoint = 'sketchPoint',
}


/**
 * 草图点框选角色有限值。
 * 说明：
 * 1. `pointKind` 只表达点是控制点还是普通草图点；
 * 2. 框选语义还需要区分“普通曲线控制点”和“需要像独立点一样参与框选的特殊控制点”；
 * 3. 使用常量对象与联合类型集中维护角色，避免放开全部控制点框选。
 */
export const SketchPointBoxSelectionRole = {
    /** 默认角色，沿用点类型的默认框选规则。 */
    Default: 'default',
    /** 矩形构造线中心交点；新数据为草图点，旧数据可能仍是需要参与框选的控制点。 */
    RectangleConstructionIntersection: 'rectangleConstructionIntersection',
    /** 倒角源线延长线的原交点焦点，需要参与框选选择。 */
    ChamferIntersectionFocus: 'chamferIntersectionFocus',
} as const;

/** 草图点框选角色类型。 */
export type SketchPointBoxSelectionRole = (
    typeof SketchPointBoxSelectionRole
)[keyof typeof SketchPointBoxSelectionRole];


/**
 * 槽口控制点角色枚举。
 * 说明：
 * 1. 直槽口有 3 个控制点（中心线起点、中心线终点、中心点）+ 4 个接合点；
 * 2. 圆弧槽口有 2 个控制点（弧线圆心、中心线弧中点）+ 4 个接合点；
 * 3. 拖拽任一点时，PointDragStrategy 根据角色重新计算槽口几何。
 */
export enum SlotControlPointRole {
    /** 中心线起始端控制点（仅直槽口）。 */
    CenterlineStart = 'centerlineStart',
    /** 中心线终止端控制点（仅直槽口）。 */
    CenterlineEnd = 'centerlineEnd',
    /** 中心控制点（仅直槽口，中心线中点）。 */
    Center = 'center',
    /** 弧线圆心控制点（仅圆弧槽口）。 */
    ArcCenter = 'arcCenter',
    /** 中心线弧中点控制点（仅圆弧槽口）。 */
    CenterArcMidpoint = 'centerArcMidpoint',
    /** 接合点 P1（topEdge 起点 / startCapArc 终点）。 */
    JunctionP1 = 'junctionP1',
    /** 接合点 P2（topEdge 终点 / endCapArc 起点）。 */
    JunctionP2 = 'junctionP2',
    /** 接合点 P3（endCapArc 终点 / bottomEdge 起点）。 */
    JunctionP3 = 'junctionP3',
    /** 接合点 P4（bottomEdge 终点 / startCapArc 起点）。 */
    JunctionP4 = 'junctionP4',
    /** 中心线弧起始端控制点（仅圆弧槽口；即起始端帽弧圆心）。 */
    CenterlineArcStart = 'centerlineArcStart',
    /** 中心线弧终止端控制点（仅圆弧槽口；即终止端帽弧圆心）。 */
    CenterlineArcEnd = 'centerlineArcEnd',
}


/**
 * 椭圆基准轴控制点角色枚举。
 * 说明：
 * 1. 一个完整椭圆有 4 个基准轴端控制点，分别位于长/短轴的正/反方向；
 * 2. 中心控制点用于整体平移椭圆；
 * 3. 拖拽任一控制点时，椭圆根据角色重新计算定义并重建几何。
 */
export enum EllipseControlPointRole {
    /** 椭圆中心点。 */
    Center = 'center',
    /** 长轴正方向端点。 */
    MajorPositive = 'majorPositive',
    /** 长轴反方向端点。 */
    MajorNegative = 'majorNegative',
    /** 短轴正方向端点。 */
    MinorPositive = 'minorPositive',
    /** 短轴反方向端点。 */
    MinorNegative = 'minorNegative',
}


/**
 * 抛物线控制点角色枚举。
 * 说明：
 * 1. SOLIDWORKS 抛物线由焦点、顶点、起点、终点定义；
 * 2. 焦点对应 PropertyManager 中的 Center/focal point 语义；
 * 3. 拖拽任一控制点时，抛物线对象会根据角色重新计算定义并回贴控制点。
 */
export enum ParabolaControlPointRole {
    /** 抛物线焦点。 */
    Focal = 'focal',
    /** 抛物线顶点。 */
    Apex = 'apex',
    /** 抛物线起点。 */
    Start = 'start',
    /** 抛物线终点。 */
    End = 'end',
}


